import { stringToRange } from 'shared-utils/src/rangeUtils';

const RANGE_KEYS = ['price', 'mqPrice', 'payment', 'mq', 'rooms', 'bathrooms', 'parkingSpaces', 'constructionYear'];

// v1.0 → v1.3 value mappings — from SM qObjectV1.0ToQObjectV1.3 transformation.
// Temporary: remove once all users have v1.3 localStorage.
const PTG_V10_TO_V13 = {
  'case': 'casa',
  'case-vacanza': 'casa',
  'terreni': 'terreno',
  'box-posti-auto': 'box-posto-auto',
  'stanze': 'stanza',
  'cantine-solai': 'cantina-solaio',
  'uffici': 'ufficio',
  'capannoni': 'capannone',
};

const CHANNEL_V10_TO_TR = {
  'affitti': 'affitto',
  'buy': 'vendita',
  'rent': 'affitto',
};

const FURNITURE_V10_TO_V13 = {
  'none': 'no',
  'partially': 'parzialmente',
  'full': 'completamente',
};

const LEVEL_V10_TO_V13 = {
  'piano terra': 0,
  'piano+terra': 0,
  'intermedio': 999,
  'ultimo': 1000,
};

// v1.0 category/channel values that force tr=affitto-breve regardless of channel.
const PTG_AFFITTO_BREVE = { 'case-vacanza': true, 'vacanze': true };

// Enum sets for SM v1.3 fields — values outside these are filtered out.
const TR_ENUM = { vendita: true, affitto: true, 'affitto-breve': true };
const CONDITION_ENUM = { nuovo: true, ristrutturato: true, abitabile: true, 'da-ristrutturare': true };
const HEATING_ENUM = { centralizzato: true, autonomo: true };
const BALCONY_TERRACE_ENUM = { balcone: true, terrazzo: true };
const PUBLISHER_ENUM = { agenzie: true, privati: true };
const GARDEN_ENUM = { comune: true, privato: true };
const FURNITURE_ENUM = { no: true, parzialmente: true, completamente: true };
const LICENSE_ENUM = {
  'bar-pub-caffe': true, 'ristorazione': true, 'negozio': true, 'tabaccheria': true,
  'laboratorio': true, 'struttura-ricettiva': true, 'estetica-e-bellezza': true, 'altre-attivita': true,
};
const PROPERTY_TYPES_ENUM = {
  'appartamento': true, 'attico-mansarda': true, 'baita-bungalow-chalet-trullo': true,
  'casa-bi-trifamiliare': true, 'casa-indipendente': true, 'loft': true,
  'rustico-casale': true, 'villa': true, 'villetta-a-schiera': true,
  'negozio-locale-commerciale': true, 'attivita-licenza-commerciale': true,
  'terreno-agricolo': true, 'terreno-edificabile': true,
};
const AVAILABILITY_ENUM = { occupato: true, libero: true, 'nuda-proprieta': true, affittato: true };
const ENERGY_CLASS_ENUM = { A: true, B: true, C: true, D: true, E: true, F: true, G: true };
const RENT_TYPE_ENUM = { seasonalRental: true, residential: true };
const PUBLICATION_DT_ENUM = { '2d': true, '7d': true, '30d': true };

const filterEnum = (arr, enumSet) => {
  if (!arr || !Array.isArray(arr)) return null;
  const filtered = arr.filter((v) => enumSet[v]);
  return filtered.length > 0 ? filtered : null;
};

const checkEnum = (val, enumSet) => (val && enumSet[val] ? val : null);

const safeNumber = (v) => {
  if (v === 'all' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const migrateCondition = (arr) => {
  if (!arr || !Array.isArray(arr)) return null;
  const expanded = [];
  arr.forEach((v) => {
    if (v === 'nuovo-ristrutturato') {
      expanded.push('nuovo', 'ristrutturato');
    } else {
      expanded.push(v);
    }
  });
  return filterEnum(expanded, CONDITION_ENUM);
};

const migrateFurniture = (arr) => {
  if (!arr || !Array.isArray(arr)) return null;
  const converted = arr.map((v) => FURNITURE_V10_TO_V13[v] || v);
  return filterEnum(converted, FURNITURE_ENUM);
};

const migrateLevel = (val) => {
  if (val === 'all' || val === undefined || val === null) return undefined;
  if (typeof val === 'number') return Number.isNaN(val) ? undefined : val;
  const converted = LEVEL_V10_TO_V13[val];
  if (converted !== undefined) return converted;
  const n = Number(val);
  return Number.isNaN(n) ? undefined : n;
};

const rangeFromMinMax = (min, max) => {
  return { min: safeNumber(min), max: safeNumber(max) };
};

const singleToRange = (val) => {
  if (val === 'all' || val === undefined || val === null) return { min: null, max: null };
  const n = safeNumber(val);
  return { min: n, max: null };
};

const mapArrayFilter = (arr) => {
  if (!arr || !Array.isArray(arr)) return null;
  if (arr.length === 1 && arr[0] === 'all') return null;
  return arr;
};

const hasV10Keys = (f) =>
  f.category !== undefined || f.channel !== undefined || f.priceMin !== undefined;

// v1.0 had separate balcony/terrace booleans AND balconyAndTerrace array.
// SM unions them: balconyAndTerrace takes priority; balcony/terrace booleans are fallback.
const migrateBalconyTerrace = (f) => {
  if (f.balconyAndTerrace) return mapArrayFilter(f.balconyAndTerrace);
  const parts = [];
  if (f.balcony && f.balcony !== 'all') parts.push('balcone');
  if (f.terrace && f.terrace !== 'all') parts.push('terrazzo');
  return parts.length > 0 ? parts : null;
};

const migrateV10ToV13 = (f) => {
  const ptgSource = f.category || f.propertyTypeGroup;
  // case-vacanza/vacanze force tr=affitto-breve regardless of channel (SM oneOf rule).
  const trFromPtg = PTG_AFFITTO_BREVE[ptgSource] ? 'affitto-breve' : null;
  const trRaw = trFromPtg || (f.channel && (CHANNEL_V10_TO_TR[f.channel] || f.channel)) || f.tr || 'vendita';
  const result = {
    propertyTypeGroup: (ptgSource && (PTG_V10_TO_V13[ptgSource] || ptgSource)) || 'casa',
    tr: TR_ENUM[trRaw] ? trRaw : 'vendita',

    price: rangeFromMinMax(f.priceMin, f.priceMax),
    mq: rangeFromMinMax(f.mqMin, f.mqMax),
    mqPrice: rangeFromMinMax(f.mqpriceMin, f.mqpriceMax),
    payment: rangeFromMinMax(f.paymentMin, f.paymentMax),
    rooms: rangeFromMinMax(f.numRoomsMin, f.numRoomsMax),
    constructionYear: rangeFromMinMax(f.buildingYearMin, f.buildingYearMax),
    bathrooms: singleToRange(f.numBaths),
    parkingSpaces: singleToRange(f.numParkingSpaces),

    hasPhoto: !!(f.photo || f.onlyWithPhoto),
    hasAC: f.air_conditioned,
    isLux: !!f.is_lux,
    hasSwimmingPool: !!f.has_swimming_pool,
    hasVirtualTour: !!f.has_virtual_tour,
    hasReception: !!f.has_reception,

    publisher: filterEnum(mapArrayFilter(f.sellerType), PUBLISHER_ENUM),
    balconyTerrace: filterEnum(migrateBalconyTerrace(f), BALCONY_TERRACE_ENUM),
    heating: filterEnum(mapArrayFilter(f.heatingType), HEATING_ENUM),
    condition: migrateCondition(f.buildingCondition),
    licenseTypeGroups: filterEnum(f.license_type_groups, LICENSE_ENUM) || [],

    propertyTypes: filterEnum(f.propertyTypes, PROPERTY_TYPES_ENUM) || [],
    zones: f.zones || [],
    sortType: f.sortType || null,
    priceType: f.priceType || 'price',
  };

  // Only include optional fields when explicitly set in v1.0 record.
  // Omitting them lets defaultFiltersForSearchBuilder values win via the HP spread.
  if (f.furniture !== undefined) result.furniture = migrateFurniture(f.furniture);
  if (f.garden !== undefined) result.garden = filterEnum(f.garden, GARDEN_ENUM) || [];
  if (f.commercial_building_location !== undefined) result.commercial_building_location = f.commercial_building_location;
  const migratedLevel = migrateLevel(f.level);
  if (migratedLevel !== undefined) result.level = migratedLevel;
  if (f.lift !== undefined && f.lift !== 'all') {
    if (typeof f.lift === 'number') result.liftIfAtLeast = Number.isNaN(f.lift) ? undefined : f.lift;
    else result.lift = !!f.lift;
  }
  if (f.availability !== undefined) {
    const av = checkEnum(f.availability, AVAILABILITY_ENUM);
    if (av) result.availability = av;
  }
  if (f.energyClass !== undefined) {
    const ec = checkEnum(f.energyClass, ENERGY_CLASS_ENUM);
    if (ec) result.energyClass = ec;
  }
  if (f.rent_type !== undefined) {
    const rt = checkEnum(f.rent_type, RENT_TYPE_ENUM);
    if (rt) result.rentType = rt;
  }
  if (f.publication_date !== undefined) {
    const pd = checkEnum(f.publication_date, PUBLICATION_DT_ENUM);
    if (pd) result.publicationDt = pd;
  }
  if (f.includes_property_ownership !== undefined) result.includesPropertyOwnership = !!f.includes_property_ownership;

  // SM: exclude_X:true → isX:false; only_X:true → isX:true. Only set when source is explicitly true.
  if (f.exclude_auction === true) result.isAuction = false;
  else if (f.only_auction === true) result.isAuction = true;
  if (f.exclude_private_negotiation === true) result.isPrivateNegotiation = false;
  else if (f.only_private_negotiation === true) result.isPrivateNegotiation = true;
  if (f.exclude_under_construction === true) result.isUnderConstruction = false;

  return result;
}

/**
 * Parse resume_last_search_record from localStorage.
 * Supports both v1.3 format ({qObject, _v: '1.3'}) and v1.0/legacy format ({filters}).
 *
 * Returns { lsFiltersRaw, rawQObject } or null if lastSearch is falsy.
 * rawQObject is non-null only for v1.3 records (for setRawQObject calls in the component).
 */
export function parseResumeRecord(lastSearch) {
  if (!lastSearch) return null;

  if (lastSearch._v === '1.3' && lastSearch.qObject) {
    const qObj = lastSearch.qObject;
    const lsFiltersRaw = Object.assign({}, qObj);

    RANGE_KEYS.forEach(function(key) {
      lsFiltersRaw[key] = typeof qObj[key] === 'string'
        ? stringToRange(qObj[key])
        : (qObj[key] || { min: null, max: null });
    });

    if (Array.isArray(lsFiltersRaw.publisher)) lsFiltersRaw.publisher = lsFiltersRaw.publisher[0] || null;
    if (!lsFiltersRaw.propertyTypes) lsFiltersRaw.propertyTypes = [];
    if (lsFiltersRaw.propertyTypes && !Array.isArray(lsFiltersRaw.propertyTypes)) {
      lsFiltersRaw.propertyTypes = [lsFiltersRaw.propertyTypes];
    }

    // qObject v1.3 uses isPrivateNegotiation (boolean) but DEFAULT_FILTERS_CONFIGURATION
    // uses excludePrivateNegotiation/onlyPrivateNegotiation. Convert so FilterNext renders them.
    if (lsFiltersRaw.isPrivateNegotiation === false) {
      lsFiltersRaw.excludePrivateNegotiation = true;
      delete lsFiltersRaw.isPrivateNegotiation;
    } else if (lsFiltersRaw.isPrivateNegotiation === true) {
      lsFiltersRaw.onlyPrivateNegotiation = true;
      delete lsFiltersRaw.isPrivateNegotiation;
    }

    // qObject v1.3 stores array filters as null when unset; coerce to [] so MultipleButtons
    // doesn't receive null as initial value (crashes on indexOf/map on first interaction).
    ['condition', 'heating', 'balconyTerrace', 'furniture', 'garden', 'commercial_building_location'].forEach(function(k) {
      if (lsFiltersRaw[k] === null) lsFiltersRaw[k] = [];
    });

    // qObject v1.3 uses liftIfAtLeast for "ascensore se piano almeno N°" but FilterInputs
    // reads the synthetic lift_level key. Convert so the sub-filter value is restored.
    if (lsFiltersRaw.liftIfAtLeast != null) {
      lsFiltersRaw.lift = true;
      lsFiltersRaw.lift_level = lsFiltersRaw.liftIfAtLeast;
      delete lsFiltersRaw.liftIfAtLeast;
    }

    return { lsFiltersRaw, rawQObject: qObj };
  }

  const v10Filters = lastSearch.filters;
  if (!v10Filters) return null;
  if (hasV10Keys(v10Filters)) {
    return { lsFiltersRaw: migrateV10ToV13(v10Filters), rawQObject: null };
  }
  return { lsFiltersRaw: v10Filters, rawQObject: null };
}
